import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown } from 'lucide-react';
import styles from './Pagination.module.css';

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 12,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [6, 12, 24, 48],
  showPageSizeSelector = true,
  showJumpToPage = true,
  showItemsInfo = true,
  maxVisiblePages = 5,
  className = ''
}) => {
  const [jumpToPageValue, setJumpToPageValue] = useState('');
  const [showJumpInput, setShowJumpInput] = useState(false);
  const [isPageSizeOpen, setIsPageSizeOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState('down');
  const pageSizeRef = useRef(null);

  // Close page size dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target)) {
        setIsPageSizeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if dropdown should appear above or below
  useEffect(() => {
    if (isPageSizeOpen && pageSizeRef.current) {
      const rect = pageSizeRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 200 && spaceAbove > 200) {
        setDropdownPosition('up');
      } else {
        setDropdownPosition('down');
      }
    }
  }, [isPageSizeOpen]);

  // Reset jump input when current page changes
  useEffect(() => {
    setJumpToPageValue('');
    setShowJumpInput(false);
  }, [currentPage]);

  // Calculate visible page numbers
  const getVisiblePages = () => {
    if (totalPages <= 0) return [];
    
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  // Handle page navigation
  const handlePageChange = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    if (newPage !== currentPage && typeof onPageChange === 'function') {
      onPageChange(newPage);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    if (onPageSizeChange && !isNaN(size)) {
      onPageSizeChange(size);
      setIsPageSizeOpen(false);
      handlePageChange(1); // Reset to first page
    }
  };

  // Handle jump to page
  const handleJumpToPage = (e) => {
    e.preventDefault();
    const page = parseInt(jumpToPageValue, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      handlePageChange(page);
    }
    setShowJumpInput(false);
  };

  // Don't render if there's only one page and no items
  if (totalPages <= 1 && totalItems <= pageSize) {
    return null;
  }

  // Calculate items range
  const startItem = totalItems > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  const visiblePages = getVisiblePages();
  const hasPrevious = currentPage > 1;
  const hasNext = currentPage < totalPages;

  return (
    <div className={`${styles.paginationContainer} ${className}`}>
      {/* Items info */}
      {showItemsInfo && totalItems > 0 && (
        <div className={styles.itemsInfo}>
          <span className={styles.itemsText}>
            Showing {startItem}-{endItem} of {totalItems} resources
          </span>
        </div>
      )}

      <div className={styles.paginationControls}>
        {/* First page button */}
        <button
          type="button"
          onClick={() => handlePageChange(1)}
          disabled={!hasPrevious}
          className={`${styles.pageButton} ${styles.navButton}`}
          aria-label="Go to first page"
          title="First page"
        >
          <ChevronsLeft size={16} aria-hidden="true" />
        </button>

        {/* Previous page button */}
        <button
          type="button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={!hasPrevious}
          className={`${styles.pageButton} ${styles.navButton}`}
          aria-label="Go to previous page"
          title="Previous page"
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>

        {/* Page numbers */}
        <div className={styles.pageNumbers}>
          {visiblePages[0] > 1 && (
            <>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                className={`${styles.pageButton} ${1 === currentPage ? styles.currentPage : ''}`}
                aria-label="Go to page 1"
                aria-current={1 === currentPage ? 'page' : undefined}
              >
                1
              </button>
              {visiblePages[0] > 2 && (
                <span className={styles.ellipsis} aria-hidden="true">
                  ...
                </span>
              )}
            </>
          )}

          {visiblePages.map((page) => (
            <button
              key={page}
              type="button"
              onClick={() => handlePageChange(page)}
              className={`${styles.pageButton} ${
                page === currentPage ? styles.currentPage : ''
              }`}
              aria-label={`Go to page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page}
            </button>
          ))}

          {visiblePages[visiblePages.length - 1] < totalPages && (
            <>
              {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                <span className={styles.ellipsis} aria-hidden="true">
                  ...
                </span>
              )}
              <button
                type="button"
                onClick={() => handlePageChange(totalPages)}
                className={`${styles.pageButton} ${totalPages === currentPage ? styles.currentPage : ''}`}
                aria-label={`Go to page ${totalPages}`}
                aria-current={totalPages === currentPage ? 'page' : undefined}
              >
                {totalPages}
              </button>
            </>
          )}
        </div>

        {/* Next page button */}
        <button
          type="button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={!hasNext}
          className={`${styles.pageButton} ${styles.navButton}`}
          aria-label="Go to next page"
          title="Next page"
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>

        {/* Last page button */}
        <button
          type="button"
          onClick={() => handlePageChange(totalPages)}
          disabled={!hasNext}
          className={`${styles.pageButton} ${styles.navButton}`}
          aria-label="Go to last page"
          title="Last page"
        >
          <ChevronsRight size={16} aria-hidden="true" />
        </button>

        {/* Page size selector */}
        {showPageSizeSelector && (
          <div className={styles.pageSizeWrapper} ref={pageSizeRef}>
            <div 
              className={styles.pageSizeSelector}
              onClick={() => setIsPageSizeOpen(!isPageSizeOpen)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsPageSizeOpen(!isPageSizeOpen);
                } else if (e.key === 'Escape') {
                  setIsPageSizeOpen(false);
                }
              }}
              aria-haspopup="listbox"
              aria-expanded={isPageSizeOpen}
              aria-label="Items per page"
            >
              <span>{pageSize} per page</span>
              <ChevronDown 
                size={16} 
                className={`${styles.pageSizeChevron} ${isPageSizeOpen ? styles.rotate180 : ''}`} 
                aria-hidden="true"
              />
            </div>
            
            {isPageSizeOpen && (
              <div 
                className={`${styles.pageSizeDropdown} ${
                  dropdownPosition === 'up' ? styles.pageSizeDropdownUp : ''
                }`}
                role="listbox"
                aria-label="Items per page options"
              >
                {pageSizeOptions.map((size) => (
                  <div
                    key={size}
                    role="option"
                    aria-selected={size === pageSize}
                    className={`${styles.pageSizeOption} ${
                      size === pageSize ? styles.pageSizeOptionActive : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePageSizeChange(size);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handlePageSizeChange(size);
                      }
                    }}
                    tabIndex={0}
                  >
                    {size} per page
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Jump to page */}
        {showJumpToPage && totalPages > 5 && (
          <div className={styles.jumpToPage}>
            {showJumpInput ? (
              <div className={styles.jumpForm}>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={jumpToPageValue}
                  onChange={(e) => setJumpToPageValue(e.target.value)}
                  className={styles.jumpInput}
                  placeholder={`1-${totalPages}`}
                  aria-label="Page number"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleJumpToPage(e);
                    } else if (e.key === 'Escape') {
                      setShowJumpInput(false);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleJumpToPage}
                  className={`${styles.pageButton} ${styles.jumpSubmit}`}
                  aria-label="Go to page"
                >
                  Go
                </button>
                <button
                  type="button"
                  onClick={() => setShowJumpInput(false)}
                  className={`${styles.pageButton} ${styles.jumpCancel}`}
                  aria-label="Cancel"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowJumpInput(true)}
                className={`${styles.pageButton} ${styles.jumpButton}`}
                aria-label="Jump to page"
              >
                Jump to page...
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Pagination;